import React from 'react';
import produce from 'immer';
import Dialog from '@material-ui/core/Dialog';
import Settings from '@material-ui/icons/Settings';
import PropTypes from 'prop-types';
import AdminTable from 'layouts/AdminTable';
import withStyles from '@material-ui/core/styles/withStyles';
import DialogContent from '@material-ui/core/DialogContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle.jsx';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import { get, post, put } from 'helpers/apiHelpers';
import { ROLE_EDIT_COMPANY } from 'helpers/roles';
import { combineStyles, isGranted } from 'helpers/helpers';
import Button from 'components/CustomButtons/Button';
import GridItem from 'components/Grid/GridItem';
// eslint-disable-next-line no-duplicate-imports -- FIXME
import ModalButton from 'components/CustomButtons/Button';
import GridContainer from 'components/Grid/GridContainer';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import withErrorHandler from 'components/HOC/withErrorHandler';
import { connect } from 'react-redux';
import Assignment from '@material-ui/icons/Assignment';
import LabeledCheckbox from 'components/Checkbox/LabeledCheckbox';
import { toast } from 'react-toastify';

import SelectInput from '../FormSelect/SelectInput';
import CardIcon from '../Card/CardIcon';
import CardHeader from '../Card/CardHeader';
import {
  hexToRgb,
  navyColor,
} from '../../assets/jss/material-dashboard-pro-react';

const navyRgb = hexToRgb(navyColor);

export const ReportLabelDivider = ({ label, icon }) => (
  <CardHeader color="primary" icon={true}>
    <CardIcon
      color="white"
      style={{
        background: navyColor,
        boxShadow: `0 4px 20px 0 rgba(${navyRgb}, .14), 0 7px 10px -5px rgba(${navyRgb}, .4)`,
        justifyContent: 'left',
        display: 'flex',
        alignItems: 'center',
        padding: 5,
      }}
    >
      {icon ?? <Assignment />} {label}
    </CardIcon>
  </CardHeader>
);

export const choiceTrueFalseField = ({ t, field, size = 12 }) => ({
  size,
  field,
  inputType: 'select',
  inputProps: {
    multiple: false,
    options: [
      {
        value: true,
        label: t('common.yes'),
      },
      {
        value: false,
        label: t('common.no'),
      },
    ],
  },
});

class ReportConfigurator extends React.Component {
  state = {
    modal: false,
    loading: true,
    errors: {},
    configurations: {},
  };

  toggleModal = () =>
    this.setState(prevState => ({ ...prevState, modal: !this.state.modal }));

  startLoading = () =>
    this.setState(prevState => ({ ...prevState, loading: true }));

  stopLoading = () =>
    this.setState(prevState => ({ ...prevState, loading: false }));

  componentDidMount = async () => {
    const { report } = this.props;

    if (isGranted(ROLE_EDIT_COMPANY)) {
      this.startLoading();

      const { reportsConfigurations } = await get(
        `/company/${this.props.companyId}/reports-configuration`
      );

      this.setState(
        produce(draft => {
          draft.configurations = reportsConfigurations;
        })
      );
      this.stopLoading();

      const { codeSeparator, allowPrivateDataInReport } = await get(
        `/brands/${this.props.brandId}`
      );

      this.setState(
        produce(draft => {
          if (draft.configurations[report]) {
            draft.configurations[report].codeSeparator = codeSeparator;
            draft.configurations[report].allowPrivateDataInReport =
              allowPrivateDataInReport;
          }
        })
      );
    }
  };

  handleToggle = name => {
    const { report } = this.props;
    this.setState(
      produce(draft => {
        draft.configurations[report][name] =
          !this.state.configurations[report][name];
      })
    );
  };

  handleInputChange = ({ target: { value, name } }) => {
    const { report, fields } = this.props;

    if (fields.find(({ field }) => field === name).inputType === 'number') {
      // eslint-disable-next-line no-param-reassign, radix -- FIXME
      value = parseInt(value) || 0;
    }

    this.setState(
      produce(draft => {
        draft.configurations[report][name] = value;
      })
    );
  };

  save = async () => {
    const { companyId, report } = this.props;
    this.props.errorHandlerResetState();
    this.startLoading();

    await put(`/brands/${this.props.brandId}`, {
      codeSeparator: this.state.configurations[report].codeSeparator,
      allowPrivateDataInReport:
        this.state.configurations[report].allowPrivateDataInReport,
    });

    try {
      await post('company/reports-configuration', {
        company: `/companies/${companyId}`,
        report,
        configuration: this.state.configurations[report],
      });

      toast.success(this.props.t('notify.changesSaved'));
      this.toggleModal();
    } catch (error) {
      this.props.errorHandlerCatch(error);
    }

    this.stopLoading();
  };

  fieldResolve = ({ field = '', size, inputType, inputProps = {}, name }) => {
    const { classes, report } = this.props;

    if (inputType === 'divider') {
      return <GridItem xs={size ?? 12}>{inputProps.content}</GridItem>;
    }

    if (inputType === 'select') {
      return (
        <GridItem xs={size ?? 12}>
          <SelectInput
            key={field}
            name={field}
            label={this.props.t(
              `configuration.reports.report.${report}.${field}`
            )}
            classes={classes}
            mapBy="label"
            trackBy="value"
            value={this.state.configurations[report][field]}
            handleChange={this.handleInputChange}
            {...inputProps}
          />
        </GridItem>
      );
    }

    if (inputType === 'checkbox') {
      return (
        <GridItem xs={size ?? 12}>
          <LabeledCheckbox
            name={field}
            onClick={() => this.handleToggle(name)}
            checked={this.state.configurations[report][field]}
            label={this.props.t(
              `configuration.reports.report.${report}.${field}`
            )}
          />
        </GridItem>
      );
    }

    return (
      <GridItem xs={size ?? 12}>
        <FormTextInput
          key={field}
          label={this.props.t(
            `configuration.reports.report.${report}.${field}`
          )}
          classes={classes}
          name={field}
          value={this.state.configurations[report][field]}
          handleChange={this.handleInputChange}
          type={inputType}
          errors={this.props.errors}
        />
      </GridItem>
    );
  };

  render() {
    const { classes, report, fields } = this.props;
    if (this.state.configurations[report] === undefined) {
      return null;
    }

    if (this.props.forceOpen) {
      return (
        <AdminTable
          title={this.props.t(
            this.props.title || 'configuration.reports.title'
          )}
          isMarginLeft={true}
        >
          <GridContainer
            justify="flex-end"
            style={{ flexWrap: 'wrap', alignItems: 'end' }}
          >
            {fields.map(field => this.fieldResolve(field))}
          </GridContainer>
          <GridContainer justify="flex-start">
            <GridItem>
              <Button
                onClick={this.toggleModal}
                style={{ margin: '5px' }}
                disabled={this.state.loading}
                className={classes.marginRight}
              >
                {this.state.loading ? (
                  <CircularProgress />
                ) : (
                  this.props.t('form.cancel')
                )}
              </Button>
              <Button
                color="success"
                className={classes.marginLeft}
                onClick={this.save}
                disabled={this.state.loading}
              >
                {this.state.loading ? (
                  <CircularProgress />
                ) : (
                  this.props.t('form.save')
                )}
              </Button>
            </GridItem>
          </GridContainer>
        </AdminTable>
      );
    }

    return (
      <>
        <Dialog
          maxWidth="lg"
          open={this.state.modal}
          onClose={this.toggleModal}
          style={{ maxWidth: '580px', margin: '0 auto' }}
        >
          <DialogContent>
            <ModalButton
              style={{
                position: 'absolute',
                top: '0',
                right: '0',
                cursor: 'pointer',
              }}
              color="transparent"
              justIcon={true}
              round={true}
              onClick={this.toggleModal}
            >
              &times;
            </ModalButton>
          </DialogContent>
          <DialogContent>
            <AdminTable title={this.props.t('configuration.reports.title')}>
              <GridContainer
                justify="flex-start"
                style={{ flexWrap: 'wrap', alignItems: 'end' }}
              >
                {fields.map(field => this.fieldResolve(field))}
              </GridContainer>
              <GridContainer justify="flex-end">
                <GridItem>
                  <Button
                    onClick={this.toggleModal}
                    style={{ margin: '5px' }}
                    disabled={this.state.loading}
                    className={classes.marginRight}
                  >
                    {this.state.loading ? (
                      <CircularProgress size={18} />
                    ) : (
                      this.props.t('form.cancel')
                    )}
                  </Button>
                  <Button
                    color="success"
                    className={classes.marginLeft}
                    onClick={this.save}
                    disabled={this.state.loading}
                  >
                    {this.state.loading ? (
                      <CircularProgress size={18} />
                    ) : (
                      this.props.t('form.save')
                    )}
                  </Button>
                </GridItem>
              </GridContainer>
            </AdminTable>
          </DialogContent>
        </Dialog>
        <Settings
          onClick={this.toggleModal}
          style={{ verticalAlign: 'bottom', cursor: 'pointer' }}
        />
      </>
    );
  }
}

ReportConfigurator.propTypes = {
  forceOpen: PropTypes.bool,
  companyId: PropTypes.number.isRequired,
  report: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      size: PropTypes.number.isRequired,
      field: PropTypes.string.isRequired,
      inputProps: PropTypes.object,
      inputType: PropTypes.oneOf(['text', 'number', 'select']).isRequired,
      tooltip: PropTypes.string,
    })
  ),
};

const mapStateToProps = state => ({
  brandId: state.Brands.brand.id,
});

const combinedStyles = combineStyles(extendedFormsStyle, buttonsStyle);

const enhance = compose(
  withStyles(combinedStyles),
  withErrorHandler,
  withTranslation(),
  connect(mapStateToProps)
);

export default enhance(ReportConfigurator);
